import _ from 'lodash';
import { makeAutoObservable } from 'mobx';

export type ModalType =
    | 'alert'
    | 'confirm'
    | 'login'
    | 'guide'
    | 'custom'
    | 'select'
    | 'menu'
    | 'stock';

export type ModalOption = {
    type: ModalType;
    title?: string;
    titleAlign?: 'left' | 'center';
    preventClose?: boolean;
    backdrop?: boolean;
    showCloseButton?: boolean;
    alert?: ModalAlertOption;
    confirm?: ModalConfirmOption;
    guide?: ModalGuideOption;
    login?: ModalLoginOption;
    custom?: ModalCustomOption;
    select?: ModalSelectOption;
    menu?: ModalMenuOption;
    stock?: ModalStockOption;
};

export type ModalAlertOption = {
    message?: string;
    confirmText?: string;
};

export type ModalConfirmOption = {
    message?: string;
    confirmText?: string;
    cancelText?: string;
    /**
     * 체크박스 추가 옵션
     * - 체크박스를 추가해서 동의 여부를 확인
     */
    checkbox?: {
        text: string;
        required?: boolean;
    };
    /**
     * 컨펌 버튼 스타일
     * - default: 기본 스타일
     * - danger: 위험 액션 (예: 탈퇴)
     */
    confirmStyle?: 'default' | 'danger';
};

export type ModalLoginOption = {
    message?: string;
};

export type ModalGuideOption = {
    docKey: string;
};

export type ModalCustomOption = {
    renderComponent: ({ modalId }: { modalId: string }) => React.ReactNode;
};

export type ModalSelectOption = {
    selectedValue?: unknown;
    showRadio?: boolean;
    closeOnSelect?: boolean;
    confirmText?: string;
    preventClose?: boolean;
    items: {
        name: string;
        value: unknown;
        icon?: React.ReactNode;
    }[];
};

export type ModalMenuOption = {
    closeOnSelect?: boolean;
    preventClose?: boolean;
    items: {
        name: string;
        icon?: React.ReactNode;
        onSelect: ({ modalId }: { modalId: string }) => void | Promise<void>;
    }[];
};

export type ModalStockOption = {
    ticker: string;
};

export type ModalItem<T = unknown> = {
    modalId: string;
    state: 'opened' | 'closing' | 'closed';
    option: ModalOption;
    returnValue: T;
    onClose?: (returnValue: T) => void;
};

export type ModalResult<T = unknown> = {
    modalId: string;
    returnValue: T;
};

class ModalStore {
    modalMap: Map<
        string,
        {
            modalItem: ModalItem;
            /**
             * 모달 엘리먼트 참조
             * 모달이 열릴 때 할당됨
             */
            modalElement: HTMLDialogElement | null;
        }
    > = new Map();

    constructor() {
        makeAutoObservable(this);
    }

    get isModalOpen() {
        return this.modalMap.size > 0;
    }

    /**
     * 모달 등록
     */
    registerModal<T = unknown>(modalId: string, option: ModalOption) {
        this.modalMap.set(modalId, {
            modalItem: {
                modalId,
                state: 'closed',
                option,
                returnValue: undefined as T,
            },
            modalElement: null,
        });
    }

    /**
     * 모달 제거
     */
    unregisterModal(modalId: string) {
        this.modalMap.delete(modalId);
    }

    /**
     * 모달 열기
     * - 상태: opened -> closing -> closed (애니메이션 종료 후 실제 닫기)
     */
    async open<T = unknown>(modalId: string): Promise<ModalResult<T>> {
        const obj = this.modalMap.get(modalId);
        const modalItem = obj?.modalItem;

        if (modalItem) {
            modalItem.returnValue = undefined as T; // 초기화
            modalItem.state = 'opened';

            const promise = new Promise<T>((resolve) => {
                modalItem.onClose = (returnValue: unknown) => {
                    resolve(returnValue as T);
                };
            });

            return {
                modalId,
                returnValue: await promise,
            };
        }

        return {
            modalId,
            returnValue: undefined as T,
        };
    }

    /**
     * 모달 닫기
     * - 모달을 닫을 땐 실제로 렌더링된 엘리먼트를 이용해서 닫음
     * - 이렇게 해야 애니메이션이 적용되고 기존 닫기 프로세스를 유지할 수 있음
     */
    async close(modalId: string) {
        const obj = this.modalMap.get(modalId);
        const modalElement = obj?.modalElement;
        if (modalElement) {
            modalElement.close();
        }
    }

    /**
     * 모달 닫기 준비 (애니메이션을 위함)
     * - opened -> closing
     */
    prepareClose(modalId: string) {
        const obj = this.modalMap.get(modalId);
        const modalItem = obj?.modalItem;

        if (modalItem) {
            modalItem.state = 'closing';
        }
    }

    /**
     * 모달 닫기
     * - closing -> closed
     */
    completeClose(modalId: string) {
        const obj = this.modalMap.get(modalId);
        const modalItem = obj?.modalItem;

        if (modalItem) {
            modalItem.state = 'closed';
            obj.modalElement = null;

            this.modalMap.delete(modalId);
        }
    }

    /**
     * 모달 반환값 설정
     * - 모달을 닫기 전에 호출해주면 됨
     */
    setReturnValue<T>(modalId: string, returnValue: T) {
        const obj = this.modalMap.get(modalId);
        const modalItem = obj?.modalItem;

        if (modalItem) {
            modalItem.returnValue = returnValue;
            obj.modalElement?.close();
        }
    }

    setModalElement(modalId: string, modalElement: HTMLDialogElement) {
        const obj = this.modalMap.get(modalId);
        if (obj && modalElement) {
            obj.modalElement = modalElement;
        }
    }

    async alert({
        title,
        message,
        confirmText = '확인',
    }: {
        title?: string;
        message: string;
        confirmText?: string;
    }): Promise<ModalResult<void>> {
        const modalId = _.uniqueId('modal_alert_');

        this.registerModal(modalId, {
            type: 'alert',
            title,
            backdrop: true,
            preventClose: false,
            alert: {
                message,
                confirmText,
            },
        });

        return this.open<void>(modalId);
    }

    async login({
        title = '로그인하고 계속하기',
    }: {
        title?: string;
    } = {}): Promise<ModalResult<boolean>> {
        const modalId = _.uniqueId('modal_login_');

        this.registerModal(modalId, {
            type: 'login',
            title,
            titleAlign: 'center',
            backdrop: true,
            preventClose: true,
            login: {},
        });

        return this.open<boolean>(modalId);
    }

    async confirm({
        title,
        message,
        confirmText = '확인',
        cancelText = '취소',
        preventClose = true, // 컨펌은 바로 닫히는 것 방지
        checkbox,
        confirmStyle = 'default',
    }: {
        title?: string;
        message: string;
        confirmText?: string;
        cancelText?: string;
        preventClose?: boolean;
        checkbox?: {
            text: string;
            required?: boolean;
        };
        confirmStyle?: 'default' | 'danger';
    }): Promise<ModalResult<boolean>> {
        const modalId = _.uniqueId('modal_confirm_');

        this.registerModal(modalId, {
            type: 'confirm',
            title,
            backdrop: true,
            preventClose,
            confirm: {
                message,
                confirmText,
                cancelText,
                checkbox,
                confirmStyle,
            },
        });

        return this.open<boolean>(modalId);
    }

    async guide({ docKey }: { docKey: string }): Promise<ModalResult<void>> {
        const modalId = _.uniqueId('modal_guide_');

        this.registerModal(modalId, {
            type: 'guide',
            backdrop: true,
            preventClose: false,
            showCloseButton: true,
            guide: {
                docKey,
            },
        });

        return this.open<void>(modalId);
    }

    async custom<T = unknown>({
        title,
        renderComponent,
        showCloseButton = true,
        preventClose = false,
    }: {
        title?: string;
        renderComponent: ({ modalId }: { modalId: string }) => React.ReactNode;
        showCloseButton?: boolean;
        preventClose?: boolean;
    }): Promise<ModalResult<T>> {
        const modalId = _.uniqueId('modal_custom_');

        this.registerModal(modalId, {
            type: 'custom',
            title,
            backdrop: true,
            preventClose,
            showCloseButton,
            custom: {
                renderComponent,
            },
        });

        return this.open<T>(modalId);
    }

    async select<T = unknown>({
        title,
        selectedValue,
        showRadio = true,
        closeOnSelect = true,
        confirmText,
        preventClose = false,
        items,
    }: {
        title?: string;
        selectedValue?: T;
        showRadio?: boolean;
        closeOnSelect?: boolean;
        confirmText?: string;
        preventClose?: boolean;
        items: {
            name: string;
            value: T;
            icon?: React.ReactNode;
        }[];
        showCloseButton?: boolean;
    }): Promise<ModalResult<T>> {
        const modalId = _.uniqueId('modal_select_');

        this.registerModal(modalId, {
            type: 'select',
            title,
            backdrop: true,
            preventClose,
            select: {
                selectedValue,
                showRadio,
                closeOnSelect,
                confirmText,
                preventClose,
                items,
            },
        });

        return this.open<T>(modalId);
    }

    async menu({
        title,
        closeOnSelect = true,
        preventClose = false,
        items,
    }: {
        title?: string;
        closeOnSelect?: boolean;
        preventClose?: boolean;
        items: {
            name: string;
            icon?: React.ReactNode;
            onSelect: ({ modalId }: { modalId: string }) => void | Promise<void>;
        }[];
    }): Promise<ModalResult<void>> {
        const modalId = _.uniqueId('modal_menu_');

        this.registerModal(modalId, {
            type: 'menu',
            title,
            backdrop: true,
            preventClose,
            menu: {
                closeOnSelect,
                preventClose,
                items,
            },
        });

        return this.open<void>(modalId);
    }

    async stock({ ticker }: { ticker: string }): Promise<ModalResult<void>> {
        const modalId = _.uniqueId('modal_stock_');

        this.registerModal(modalId, {
            type: 'stock',
            backdrop: true,
            preventClose: false,
            showCloseButton: true,
            stock: {
                ticker,
            },
        });

        return this.open<void>(modalId);
    }
}

export default new ModalStore();
